import { Link } from "react-router-dom";
import { Hexagon, ChevronRight, ShieldCheck, HandCoins, Eye } from "lucide-react";
import SeoHead from "@/components/SeoHead";
import AffiliateCTA from "@/components/AffiliateCTA";
import { Reveal } from "@/components/Reveal";

const PRINCIPLES = [
  {
    icon: Eye,
    title: "No fake proof",
    text: "No stock-photo testimonials, no invented screenshots. Stats on this site link to sources you can check yourself.",
  },
  {
    icon: ShieldCheck,
    title: "Weaknesses included",
    text: "Honeygain's $20 threshold is the highest in its category, and we say so — on the homepage and in our comparison guide.",
  },
  {
    icon: HandCoins,
    title: "Commission explained",
    text: "When you join through a link here, Honeygain pays a referral commission. It never comes out of your earnings, and you keep the $3 bonus.",
  },
];

const About = () => (
  <div className="min-h-screen bg-[#FAF8F5] honey-grain font-body text-zinc-900">
    <SeoHead
      title="About HoneyGain Deals — who runs this site and why"
      description="HoneyGain Deals is an independent affiliate page about earning passive income with Honeygain. Here's who runs it, how the commission works, and what we promise."
      path="/about"
    />
    <header className="border-b border-amber-100 bg-white/80 backdrop-blur-md sticky top-0 z-40">
      <div className="mx-auto max-w-3xl px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-display font-extrabold text-lg text-zinc-900" data-testid="about-logo">
          <span className="grid place-items-center w-8 h-8 rounded-xl bg-amber-400 text-zinc-900">
            <Hexagon className="w-5 h-5" strokeWidth={2.5} />
          </span>
          HoneyGain<span className="text-amber-600">Deals</span>
        </Link>
        <AffiliateCTA label="Get $3 Bonus" testid="about-header-cta" dark />
      </div>
    </header>

    <main className="mx-auto max-w-3xl px-5 sm:px-8 py-14 sm:py-20" data-testid="about-page">
      <nav className="flex items-center gap-1.5 text-xs font-semibold text-zinc-500">
        <Link to="/" className="hover:text-amber-700 transition-colors">Home</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-amber-700">About</span>
      </nav>

      <Reveal>
        <h1 className="mt-6 font-display text-3xl sm:text-5xl font-extrabold tracking-tight leading-[1.1]">
          About HoneyGain Deals
        </h1>
        <p className="mt-6 text-base sm:text-lg text-zinc-600 leading-relaxed">
          HoneyGain Deals is an independent affiliate page run by someone who actually uses Honeygain.
          It exists for one reason: bandwidth sharing is the easiest entry point into passive income,
          and most pages about it either oversell it or hide how they make money.
        </p>
        <p className="mt-4 text-base sm:text-lg text-zinc-600 leading-relaxed">
          When you join Honeygain through a link on this site, Honeygain pays the owner a referral
          commission. That commission never comes out of your balance — and because you joined through
          a referral link, you start with a $3 bonus you would not get by signing up directly.
        </p>
      </Reveal>

      <div className="mt-12 grid gap-5 sm:grid-cols-3">
        {PRINCIPLES.map(({ icon: Icon, title, text }, i) => (
          <Reveal key={title} delay={i * 0.1}>
            <div className="h-full rounded-3xl border border-zinc-100 bg-white p-6 shadow-sm" data-testid="about-principle">
              <div className="grid place-items-center w-10 h-10 rounded-xl bg-amber-400 text-zinc-900">
                <Icon className="w-5 h-5" />
              </div>
              <h2 className="mt-4 font-display text-base font-bold text-zinc-900">{title}</h2>
              <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <div className="mt-12 rounded-3xl border border-amber-200 bg-amber-50 p-7">
          <h2 className="font-display text-lg font-bold text-zinc-900">A note on the numbers you see here</h2>
          <p className="mt-2 text-sm text-zinc-700 leading-relaxed">
            Earnings figures on this site are realistic ranges, not promises: most users earn $5–$45 a
            month, and Honeygain reports an average payout of about $27. Where a number can be checked —
            the Trustpilot rating, the payout terms — we link straight to the source so you never have
            to take this page's word for it.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.15} className="mt-12 text-center">
        <AffiliateCTA label="Join Honeygain — Get $3 Free" testid="about-bottom-cta" />
      </Reveal>
    </main>
  </div>
);

export default About;
